import { catalogLabel, createItem } from "./catalog";
import { createProject, listMyProjects, loadProject, removeProject, saveProject, type CloudMeta } from "./cloud";
import { loadShare } from "./share";
import { defaultProject, isAddKind, migrateProject, newId, type Item, type ItemKind, type PlaceMode, type Project } from "./types";
import { setWallFromEnds } from "./wall";

const KEY = "quinta-3d-project";
const LAST_CLOUD = "mirancho-last-project";

export type Listener = () => void;

interface HistorySnap {
  project: Project;
  selectedId: string | null;
  selectedIds: string[];
  title: string;
}

export class Store {
  project: Project;
  selectedId: string | null = null;
  selectedIds: string[] = [];
  placeMode: PlaceMode = "select";
  wallStart: { x: number; z: number } | null = null;
  apertureId: string | null = null;
  wallFace: 0 | 1 | 2 | 3 | null = null;
  uid: string | null = null;
  cloudId: string | null = null;
  title = "Mi rancho";
  library: CloudMeta[] = [];
  cloudError: string | null = null;
  saving = false;
  viewOnly = false;
  shareExpiresAt: number | null = null;
  private listeners = new Set<Listener>();
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private undoStack: HistorySnap[] = [];
  private redoStack: HistorySnap[] = [];
  private editOpen = false;

  constructor() {
    this.project = this.load() ?? defaultProject();
    this.selectedId = this.project.items[0]?.id ?? null;
    this.selectedIds = this.selectedId ? [this.selectedId] : [];
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  notify(): void {
    if (!this.viewOnly) {
      this.persist();
      this.scheduleCloudSave();
    }
    for (const fn of this.listeners) fn();
  }

  selected(): Item | null {
    return this.project.items.find((i) => i.id === this.selectedId) ?? null;
  }

  selectedItems(): Item[] {
    return this.selectedIds
      .map((id) => this.project.items.find((i) => i.id === id))
      .filter((i): i is Item => Boolean(i));
  }

  select(id: string, additive = false): void {
    if (additive) {
      if (this.selectedIds.includes(id)) {
        this.selectedIds = this.selectedIds.filter((x) => x !== id);
      } else if (this.selectedIds.length >= 2) {
        this.selectedIds = [this.selectedIds[this.selectedIds.length - 1], id];
      } else {
        this.selectedIds = [...this.selectedIds, id];
      }
    } else {
      this.selectedIds = [id];
    }
    this.selectedId = this.selectedIds[this.selectedIds.length - 1] ?? null;
    this.apertureId = null;
    this.wallFace = null;
  }

  replace(project: Project): void {
    this.project = project;
    this.selectedId = project.items[0]?.id ?? null;
    this.selectedIds = this.selectedId ? [this.selectedId] : [];
    this.placeMode = "select";
    this.wallStart = null;
    this.apertureId = null;
    this.wallFace = null;
    this.notify();
  }

  reset(): void {
    this.replace(defaultProject());
  }

  checkpoint(): void {
    if (this.editOpen) return;
    this.undoStack.push(this.snapshot());
    if (this.undoStack.length > 80) this.undoStack.shift();
    this.redoStack = [];
  }

  beginEdit(): void {
    if (this.editOpen) return;
    this.checkpoint();
    this.editOpen = true;
  }

  endEdit(): void {
    if (!this.editOpen) return;
    this.editOpen = false;
    const last = this.undoStack[this.undoStack.length - 1];
    if (last && JSON.stringify(last.project) === JSON.stringify(this.project) && last.title === this.title) {
      this.undoStack.pop();
    }
  }

  undo(): boolean {
    const prev = this.undoStack.pop();
    if (!prev) return false;
    this.editOpen = false;
    this.redoStack.push(this.snapshot());
    this.applySnap(prev);
    this.notify();
    return true;
  }

  redo(): boolean {
    const next = this.redoStack.pop();
    if (!next) return false;
    this.editOpen = false;
    this.undoStack.push(this.snapshot());
    this.applySnap(next);
    this.notify();
    return true;
  }

  clearHistory(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.editOpen = false;
  }

  private snapshot(): HistorySnap {
    return {
      project: JSON.parse(JSON.stringify(this.project)) as Project,
      selectedId: this.selectedId,
      selectedIds: [...this.selectedIds],
      title: this.title,
    };
  }

  private applySnap(snap: HistorySnap): void {
    this.project = snap.project;
    this.selectedId = snap.selectedId;
    this.selectedIds = snap.selectedIds;
    this.title = snap.title;
    this.placeMode = "select";
    this.wallStart = null;
    this.apertureId = null;
    this.wallFace = null;
  }

  addAt(kind: ItemKind, x: number, z: number): Item {
    this.checkpoint();
    const parent = this.selected()?.kind === "house" ? this.selectedId ?? undefined : undefined;
    const item = createItem(kind, x, z, kind === "room" ? parent : undefined);
    const n = this.project.items.filter((i) => i.kind === kind).length + 1;
    const base = item.name.replace(/ \d+$/, "");
    item.name = n === 1 ? base : `${base} ${n}`;
    this.project.items.push(item);
    this.select(item.id);
    this.placeMode = "select";
    this.wallStart = null;
    return item;
  }

  addWall(ax: number, az: number, bx: number, bz: number): Item {
    this.checkpoint();
    const item = createItem("wall", 0, 0);
    setWallFromEnds(item, ax, az, bx, bz);
    const n = this.project.items.filter((i) => i.kind === "wall").length + 1;
    item.name = n === 1 ? "Pared" : `Pared ${n}`;
    this.project.items.push(item);
    this.select(item.id);
    this.placeMode = "select";
    this.wallStart = null;
    return item;
  }

  moveItem(id: string, x: number, z: number): void {
    const item = this.project.items.find((i) => i.id === id);
    if (!item) return;
    const dx = x - item.x;
    const dz = z - item.z;
    item.x = x;
    item.z = z;
    if (item.kind === "house") {
      for (const child of this.project.items) {
        if (child.parentId === item.id) {
          child.x += dx;
          child.z += dz;
        }
      }
    }
  }

  rotateItem(id: string, delta: number): void {
    this.checkpoint();
    const item = this.project.items.find((i) => i.id === id);
    if (!item) return;
    item.rotation += delta;
  }

  duplicateSelected(): Item[] {
    this.checkpoint();
    const sources = this.selectedItems();
    if (!sources.length) return [];
    const copies: Item[] = [];
    const idMap = new Map<string, string>();

    const clone = (src: Item, parentId?: string): Item => {
      const n = this.project.items.filter((i) => i.kind === src.kind).length + copies.length + 1;
      const copy: Item = {
        ...src,
        id: newId(),
        name: `${catalogLabel(src.kind)} ${n}`,
        x: src.x + 1.5,
        z: src.z + 1.5,
        parentId: parentId ?? src.parentId,
        openings: src.openings?.map((op) => ({ ...op, id: newId() })),
        apertures: src.apertures?.map((ap) => ({ ...ap, id: newId() })),
        cornerH: src.cornerH ? ([...src.cornerH] as [number, number, number, number]) : undefined,
      };
      idMap.set(src.id, copy.id);
      copies.push(copy);
      return copy;
    };

    for (const src of sources) clone(src);
    for (const src of sources) {
      if (src.kind !== "house") continue;
      const newParent = idMap.get(src.id);
      for (const child of this.project.items) {
        if (child.parentId === src.id && !idMap.has(child.id)) clone(child, newParent);
      }
    }
    for (const copy of copies) {
      if (copy.parentId && idMap.has(copy.parentId)) copy.parentId = idMap.get(copy.parentId);
    }

    this.project.items.push(...copies);
    const top = sources.map((s) => idMap.get(s.id)).filter((id): id is string => Boolean(id));
    this.selectedIds = top;
    this.selectedId = top[top.length - 1] ?? null;
    return copies;
  }

  removeSelectedOpening(): boolean {
    this.checkpoint();
    const item = this.selected();
    if (!item || !this.apertureId) return false;
    if (item.kind === "wall") {
      const aps = item.apertures?.length ? item.apertures : [];
      if (!aps.length && item.hasDoor) {
        item.hasDoor = false;
        item.apertures = [];
        this.apertureId = null;
        return true;
      }
      const next = aps.filter((a) => a.id !== this.apertureId);
      if (next.length === aps.length) return false;
      item.apertures = next;
      item.hasDoor = false;
      this.apertureId = next[0]?.id ?? null;
      return true;
    }
    if (!item.openings?.length) return false;
    const next = item.openings.filter((op) => op.id !== this.apertureId);
    if (next.length === item.openings.length) return false;
    item.openings = next;
    this.apertureId = next[0]?.id ?? null;
    return true;
  }

  removeSelected(): void {
    this.checkpoint();
    const ids = new Set(this.selectedIds);
    if (!ids.size && this.selectedId) ids.add(this.selectedId);
    if (!ids.size) return;
    this.project.items = this.project.items.filter((i) => !ids.has(i.id) && !ids.has(i.parentId ?? ""));
    const next = this.project.items[0]?.id ?? null;
    this.selectedIds = next ? [next] : [];
    this.selectedId = next;
    this.placeMode = "select";
    this.apertureId = null;
    this.wallFace = null;
  }

  setPlace(mode: PlaceMode): void {
    this.placeMode = this.placeMode === mode && !isAddKind(mode) ? "select" : mode;
    if (this.placeMode !== "wall") this.wallStart = null;
  }

  async bindUser(uid: string): Promise<void> {
    this.uid = uid;
    this.cloudError = null;
    this.library = await listMyProjects(uid);
    const last = localStorage.getItem(LAST_CLOUD);
    const pick = (last && this.library.some((p) => p.id === last) ? last : this.library[0]?.id) ?? null;
    if (pick) await this.openCloud(pick);
    else await this.createCloud("Mi rancho", defaultProject());
    this.clearHistory();
    this.emit();
  }

  unbindUser(): void {
    this.uid = null;
    this.cloudId = null;
    this.library = [];
    this.title = "Mi rancho";
    this.cloudError = null;
    this.project = defaultProject();
    this.selectedId = this.project.items[0]?.id ?? null;
    this.selectedIds = this.selectedId ? [this.selectedId] : [];
    this.clearHistory();
    this.emit();
  }

  async openShare(id: string): Promise<void> {
    const loaded = await loadShare(id);
    this.viewOnly = true;
    this.shareExpiresAt = loaded.expiresAt;
    this.title = loaded.title;
    this.project = loaded.data;
    this.uid = null;
    this.cloudId = null;
    this.selectedId = this.project.items[0]?.id ?? null;
    this.selectedIds = this.selectedId ? [this.selectedId] : [];
    this.placeMode = "select";
    this.clearHistory();
    this.emit();
  }

  async openCloud(id: string): Promise<void> {
    if (!this.uid) return;
    const loaded = await loadProject(id, this.uid);
    if (!loaded) {
      this.cloudError = "No pude abrir ese proyecto";
      return;
    }
    this.cloudId = id;
    this.title = loaded.title;
    this.project = loaded.data;
    this.selectedId = this.project.items[0]?.id ?? null;
    this.selectedIds = this.selectedId ? [this.selectedId] : [];
    this.placeMode = "select";
    this.wallStart = null;
    this.apertureId = null;
    this.wallFace = null;
    localStorage.setItem(LAST_CLOUD, id);
    this.persist();
    this.clearHistory();
  }

  async createCloud(title: string, data: Project): Promise<void> {
    if (!this.uid) return;
    this.cloudId = await createProject(this.uid, title, data);
    this.title = title;
    this.project = data;
    this.selectedId = data.items[0]?.id ?? null;
    this.selectedIds = this.selectedId ? [this.selectedId] : [];
    localStorage.setItem(LAST_CLOUD, this.cloudId);
    this.library = await listMyProjects(this.uid);
    this.persist();
    this.clearHistory();
  }

  async deleteCloud(): Promise<void> {
    if (!this.uid || !this.cloudId) return;
    await removeProject(this.cloudId);
    this.library = await listMyProjects(this.uid);
    const next = this.library[0];
    if (next) await this.openCloud(next.id);
    else await this.createCloud("Mi rancho", defaultProject());
  }

  renameTitle(title: string): void {
    this.title = title.trim() || "Mi rancho";
    this.notify();
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  private scheduleCloudSave(): void {
    if (!this.uid || !this.cloudId) return;
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      void this.flushCloud();
    }, 800);
  }

  private async flushCloud(): Promise<void> {
    if (!this.uid || !this.cloudId) return;
    this.saving = true;
    this.emit();
    try {
      await saveProject(this.cloudId, this.title, this.project);
      this.library = this.library.map((p) =>
        p.id === this.cloudId ? { ...p, title: this.title, updatedAt: Date.now() } : p,
      );
      this.cloudError = null;
    } catch (err) {
      this.cloudError = err instanceof Error ? err.message : "No pude guardar en la nube";
    } finally {
      this.saving = false;
      this.emit();
    }
  }

  private persist(): void {
    if (this.viewOnly) return;
    try {
      localStorage.setItem(KEY, JSON.stringify(this.project));
    } catch {
      /* ignore quota */
    }
  }

  private load(): Project | null {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return null;
      return migrateProject(JSON.parse(raw));
    } catch {
      return null;
    }
  }
}

export const store = new Store();
